import { FC, useEffect, useState } from 'react'
import { Empty } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import dayjs from '@store/timetable/dayjs.config'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import { IcoRemove } from '@/utils/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import {
  getStudentsForLessonReport,
  IStudentLessonStatus,
  saveStudentsLessonStatuses,
} from './utils/api'
import cn from './index.module.css'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import UserCard from '@pages/Admin/Manage/tabs/ClassesPage/components/ShowUsersModal/components/UserCard/UserCard'
import Preloader from '@UI/Preloader'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import { ITimetableReportItem } from '@pages/Journal2/components/Tabs/KTPJournal'
import {
  getStatusColor,
  getStatusDescription,
  LESSON_STATUS_OPTIONS,
} from '@pages/Admin/Reports/GroupLessonPayment/utils/lessonStatus'

interface ISaveStudentsLessonReportModal {
  open: boolean
  status: string
  report: ITimetableReportItem
  onDismiss: () => void
  onSuccess?: () => void
  lessonComment?: string
}

const formatLessonDate = (date?: string | null) => {
  if (!date) return '-'

  const value = dayjs(date)

  return value.isValid() ? value.format('DD.MM.YYYY в HH:mm') : '-'
}

const GROUP_PER_LESSON_STATUS_OPTIONS = LESSON_STATUS_OPTIONS.filter((option) =>
  ['5', '10', '11', '12'].includes(String(option.value)),
)

const SaveStudentsLessonReportModal: FC<ISaveStudentsLessonReportModal> = (
  props,
) => {
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => props.onDismiss()}
    >
      <Body {...props} />
    </Modal>
  )
}

const Body: FC<ISaveStudentsLessonReportModal> = ({
  status,
  report,
  open,
  onDismiss,
  onSuccess,
  lessonComment: initialLessonComment,
}) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [rows, setRows] = useState<IStudentLessonStatus[]>([])
  const [updatedStatusesInAttendanceJournal, setUpdatedStatusesInAttendanceJournal] =
    useState(true)
  const [lessonComment, setLessonComment] = useState(
    initialLessonComment ?? report?.comment ?? '',
  )
  const isOpenLesson = Number(report?.isOpen) === 1

  const lessonDate = report?.date_conference
    ? `${report.date_conference} ${report.start_time ?? ''}`.trim()
    : null

  const studentsText =
    report?.conference_type === 'group'
      ? report?.groupsText?.join(', ')
      : report?.attendeesText?.join(', ')

  const statusOptions = GROUP_PER_LESSON_STATUS_OPTIONS

  useEffect(() => {
    if (!open) return

    setRows([])
    setLessonComment(initialLessonComment ?? report?.comment ?? '')

    if (!report?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось определить занятие',
        type: 'error',
      })
      return
    }

    let isActive = true

    const loadStudents = async () => {
      setLoading(true)
      const response = await getStudentsForLessonReport(Number(report.id))

      if (!isActive) return

      setLoading(false)

      if (!response.success) {
        addAlert({
          title: 'Ошибка',
          text: response.error || 'Не удалось загрузить учеников занятия',
          type: 'error',
        })
        return
      }

      setRows(
        (response.data ?? []).map((row) => ({
          ...row,
          status,
        })),
      )
    }

    loadStudents()

    return () => {
      isActive = false
    }
  }, [initialLessonComment, report?.comment, report?.id, open, status])

  const updateRow = (
    studentId: number,
    field: 'status' | 'comment',
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.student.id === studentId ? { ...row, [field]: value } : row,
      ),
    )
  }

  const onSave = async () => {
    if (!report?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось определить занятие',
        type: 'error',
      })
      return
    }

    if (!rows.length) {
      addAlert({ title: 'Нет изменений для сохранения' })
      return
    }
    if (status !== '5' && !lessonComment.trim()) {
      addAlert({
        title: 'Нужно написать комментарий',
        type: 'error',
      })
      return
    }

    setSubmitting(true)

    const response = await saveStudentsLessonStatuses(
      Number(report.id),
      rows.map((row) => ({
        id: row.id,
          student: row.student,
          status: String(row.status ?? status),
          comment: row.comment ?? '',
        })),
        status,
        !isOpenLesson && updatedStatusesInAttendanceJournal,
        lessonComment,
      )

    setSubmitting(false)

    if (!response.success) {
      addAlert({
        title: 'Ошибка',
        text: response.error || 'Не удалось сохранить статусы учеников',
        type: 'error',
      })
      return
    }

    addAlert({ title: 'Статусы учеников сохранены' })
    onSuccess?.()
    onDismiss()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Отчитаться по ученикам за занятие
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={classNames(mclasses.formWrapper, cn.form)}>
        <div className={cn.lessonInfo}>
          <FieldWrapper label={'Курс'} className={cn.lessonInfoItem}>
            <span className={cn.lessonInfoValue}>
              {report?.direction ?? report?.directionName ?? '-'}
            </span>
          </FieldWrapper>
          <FieldWrapper label={'Группа'} className={cn.lessonInfoItem}>
            <span className={cn.lessonInfoValue}>
              {studentsText || '-'}
            </span>
          </FieldWrapper>
          <FieldWrapper label={'Дата и время'} className={cn.lessonInfoItem}>
            <span className={cn.lessonInfoValue}>
              {formatLessonDate(lessonDate)}
            </span>
          </FieldWrapper>
        </div>
        <TextAreaField
          label="Комментарий к занятию"
          value={lessonComment}
          placeholder="Комментарий"
          autoSize={{ minRows: 2, maxRows: 5 }}
          onChange={(event) => setLessonComment(event.target.value)}
        />
        {isOpenLesson && (
          <div className={cn.paymentHint}>
            <InfoCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <span>
              При статусе «Отменено» деньги возвращаются учащемуся. При
              статусе «Платная отмена» возврата не будет.
            </span>
          </div>
        )}
        {loading ? (
          <div className={cn.loading}>
            <Preloader />
          </div>
        ) : rows.length ? (
          <div className={cn.table}>
            <div className={classNames(cn.row, cn.headerRow)}>
              <span>Ученик</span>
              <span>Статус</span>
              <span>Комментарий</span>
            </div>
            <div className={cn.rows}>
              {rows.map((row) => (
                <div className={cn.row} key={row.id}>
                  <UserCard user={row.student} />
                  <div className={cn.statusCell}>
                    <span
                      className={cn.statusMarker}
                      style={{
                        backgroundColor: getStatusColor(
                          String(row.status ?? status),
                        ),
                      }}
                      title={getStatusDescription(String(row.status ?? status))}
                    />
                    <SelectField
                      className={cn.statusSelect}
                      value={String(row.status ?? status)}
                      options={statusOptions}
                      onChange={(value) =>
                        updateRow(
                          row.student.id,
                          'status',
                          String(value ?? '0'),
                        )
                      }
                    />
                  </div>
                  <TextAreaField
                    className={cn.comment}
                    value={row.comment}
                    placeholder="Комментарий"
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    onChange={(event) =>
                      updateRow(row.student.id, 'comment', event.target.value)
                    }
                  />
                </div>
              ))}
            </div>
            {!isOpenLesson && (
              <CheckboxLabel
                className={cn.attendanceCheckbox}
                label={'Проставить статусы в журнал посещаемости'}
                value={updatedStatusesInAttendanceJournal}
                onChange={setUpdatedStatusesInAttendanceJournal}
              />
            )}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ученики не найдены"
          />
        )}
      </div>
      <div className={mclasses.actions}>
        <Button color="gray" onClick={onDismiss}>
          Отменить
        </Button>
        <Button
          color="primary"
          onClick={onSave}
          isLoading={submitting}
          disabled={loading || submitting || rows.length === 0}
        >
          Сохранить
        </Button>
      </div>
    </div>
  )
}

export default SaveStudentsLessonReportModal
